import React from "react";
import { Loader2 } from "lucide-react";
import { tierAccent } from "@/lib/format";

export const cn = (...a) => a.filter(Boolean).join(" ");

export function PageHeader({ eyebrow, title, subtitle, right }) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4 fade-in">
      <div>
        {eyebrow && <div className="hsc-label mb-2">{eyebrow}</div>}
        <h1 className="font-display text-3xl text-white sm:text-4xl">{title}</h1>
        {subtitle && <p className="mt-2 max-w-xl text-sm text-[#8E8E93]">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function Card({ className, children, ...rest }) {
  return (
    <div className={cn("hsc-card p-5", className)} {...rest}>
      {children}
    </div>
  );
}

export function StatCard({ label, value, sub, accent, testId }) {
  return (
    <div className="hsc-card p-5 fade-in" data-testid={testId}>
      <div className="hsc-label">{label}</div>
      <div className="mt-2 font-display text-2xl text-white" style={accent ? { color: accent } : undefined}>
        {value}
      </div>
      {sub != null && <div className="mt-1 text-xs text-[#6E6E73]">{sub}</div>}
    </div>
  );
}

export function Button({ variant = "primary", className, children, ...rest }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold tracking-wide transition-all disabled:opacity-50 disabled:pointer-events-none";
  const variants = {
    primary: "bg-white text-black hover:bg-[#D1D1D6] active:scale-[0.98]",
    outline: "border border-[#2C2C2E] text-white hover:border-[#6E6E73] hover:bg-[#141416]",
    ghost: "text-[#D1D1D6] hover:bg-[#141416]",
    danger: "border border-[#3A3A3C] text-[#FF6B6B] hover:bg-[#1a0d0d]",
  };
  return (
    <button className={cn(base, variants[variant], className)} {...rest}>
      {children}
    </button>
  );
}

export function Pill({ active, children, ...rest }) {
  return (
    <button
      className={cn(
        "whitespace-nowrap rounded-full border px-4 py-1.5 text-[13px] transition-colors",
        active ? "border-white bg-white text-black" : "border-[#2C2C2E] text-[#A1A1A6] hover:border-[#6E6E73]"
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function TierBadge({ tier }) {
  const accent = tierAccent(tier);
  return (
    <span
      className="rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-widest"
      style={{ color: accent, borderColor: accent + "55", background: accent + "12" }}
    >
      {tier}
    </span>
  );
}

export function Loading({ label = "Memuat..." }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-[#6E6E73]">
      <Loader2 className="h-6 w-6 animate-spin" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-[#1F1F22] py-16 text-center">
      {Icon && <Icon className="h-8 w-8 text-[#3A3A3C]" />}
      <div className="font-display text-lg text-white">{title}</div>
      {subtitle && <div className="max-w-sm text-sm text-[#6E6E73]">{subtitle}</div>}
      {action}
    </div>
  );
}

export function ErrorText({ children }) {
  if (!children) return null;
  return <div className="rounded-lg border border-[#3A3A3C] bg-[#160b0b] px-3 py-2 text-sm text-[#FF8080]">{children}</div>;
}

const SEVERITY_COLORS = {
  CRITICAL: "#FF6B6B", WARNING: "#E7C25B", INFO: "#8FD3E8",
  PAID: "#7CD992", PARTIAL: "#E7C25B", UNPAID: "#FF6B6B",
  valid: "#7CD992", expiring: "#E7C25B", expired: "#FF6B6B",
};
export function SeverityTag({ value }) {
  const c = SEVERITY_COLORS[value] || "#8E8E93";
  return (
    <span className="rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider"
      style={{ color: c, background: c + "18" }}>
      {value}
    </span>
  );
}

export function Modal({ open, onClose, title, children, maxWidth = "max-w-lg" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className={cn("relative w-full rounded-2xl border border-[#2C2C2E] bg-[#0A0A0C] p-6 shadow-2xl fade-in", maxWidth)}
        onClick={(e) => e.stopPropagation()}
      >
        {title && <div className="mb-4 font-display text-xl text-white">{title}</div>}
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <div className="hsc-label mb-1.5">{label}</div>
      {children}
    </label>
  );
}
